import * as React from "react";
import { I18n } from "react-redux-i18n";

import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from "@common/components/modal";
import { FormControl } from "@common/components/form-control";
import { Button } from "@common/components/Button";
import { Flex } from "@common/components/Flex";
import { getTestAttributes } from "@common/utils/tests";
import { ImportFileInput } from "./components/file-input";

import { HttpErrorDto } from "@common/dto/http";

import "./pedigree-import.styles.scss";

export type PedigreesImportProps = {
  onClose: () => void;
  showErrorMessage: (message: string) => void;
  onSubmit: ({
    pedigreeName,
    file,
  }: {
    pedigreeName: string;
    file: File;
  }) => Promise<void>;
};

export const PedigreeImport: React.FC<PedigreesImportProps> = (props) => {
  const [pedigreeName, setPedigreeName] = React.useState("");
  const [pedigreeFile, setPedigreeFile] = React.useState<File>();
  const [loading, setLoading] = React.useState(false);

  const disabledSubmit = loading || !pedigreeName.trim().length || !pedigreeFile;

  const handleUpload = React.useCallback((file: File) => {
    const defaultPedigreeName = `C:\\fakepath\\${file.name}`.split(".xml")[0] as string;
    setPedigreeName(pedigreeName.trim().length ? pedigreeName : defaultPedigreeName);
    setPedigreeFile(file);
  }, [pedigreeName]);

  const handlePedigreeNameChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPedigreeName(e.target.value);
    },
    []
  );

  const handleSubmit = async () => {
    if (disabledSubmit) return;

    try {
      setLoading(true);
      await props.onSubmit({ pedigreeName, file: pedigreeFile });
    } catch (err) {
      console.error(err);

      props.showErrorMessage(
        (err as HttpErrorDto)?.entity || I18n.t("Operation failed")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show
      className="FoldersPedigreeImport"
      bsSize="small"
      bsClass="FolderManagement bootstrap modal"
      onHide={props.onClose}
      onEscapeKeyDown={props.onClose}
      animation={false}
    >
      <ModalHeader closeButton onHide={props.onClose}>
        <ModalTitle {...getTestAttributes({ id: "lbl-ped-import-header" })}>
          {I18n.t("Import")}
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Flex alignItems="center">
          <label
            className="FoldersPedigreeImport__pedigree-label"
            {...getTestAttributes({ id: "tx-ped-import-ped-name" })}
          >
            {I18n.t("Pedigree Name")}:
          </label>
          <div className="FoldersPedigreeImport__pedigree-name">
            <FormControl
              value={pedigreeName}
              {...getTestAttributes({
                id: "tx-ped-import-ped-namee",
              })}
              onChange={handlePedigreeNameChange}
            />
          </div>
        </Flex>
        <Flex>
        <ImportFileInput onUpload={handleUpload} />
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Flex justify="flex-end">
          <Button
            {...getTestAttributes({ id: "btn-ped-import-cancel" })}
            disabled={loading}
            onClick={props.onClose}
          >
            {I18n.t("Cancel")}
          </Button>
          <Button
            {...getTestAttributes({ id: "btn-ped-import-continue" })}
            disabled={disabledSubmit}
            onClick={handleSubmit}
            bsStyle="primary"
          >
            {I18n.t("Import")}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};
